import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertTriangle, CheckCircle, Clock, XCircle, Eye, Trash2, RefreshCw, Square, CheckSquare } from 'lucide-react';
import { ReAnalysisHistoryService, ReAnalysisSession, SessionFilters } from '@/services/ReAnalysisHistoryService';
import { PatternComparisonService } from '@/services/PatternComparisonService';
import { useToast } from '@/hooks/use-toast';
import { formatDistanceToNow } from 'date-fns';

interface ReAnalysisSessionsTableProps {
  onSessionSelect?: (sessionId: string, fileId: string) => void;
  refreshTrigger?: number;
}

export const ReAnalysisSessionsTable: React.FC<ReAnalysisSessionsTableProps> = ({
  onSessionSelect,
  refreshTrigger
}) => {
  const [sessions, setSessions] = useState<ReAnalysisSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState<ReAnalysisSession | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [comparisonData, setComparisonData] = useState<any>(null);
  const [loadingComparison, setLoadingComparison] = useState(false);
  const [filters, setFilters] = useState<SessionFilters>({});
  const [selectedSessions, setSelectedSessions] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const { toast } = useToast();

  const loadSessions = async () => {
    setLoading(true);
    try {
      const result = await ReAnalysisHistoryService.getSessions(filters);
      if (result.success) {
        setSessions(result.data || []);
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: result.error || "Failed to load sessions"
        });
      }
    } catch (error) {
      console.error('Error loading sessions:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load re-analysis sessions"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSessions();
  }, [refreshTrigger, filters]);

  const handleViewDetails = async (session: ReAnalysisSession) => {
    setSelectedSession(session);
    setShowDetailModal(true);
    setComparisonData(null); // Reset previous data
    
    // Comparison disabled - backup feature removed for optimization
  };

  const handleDeleteSession = async (sessionId: string) => {
    if (!confirm('Are you sure you want to delete this session?')) return;
    
    try {
      const result = await ReAnalysisHistoryService.deleteSession(sessionId);
      if (result.success) {
        toast({
          title: "Success",
          description: "Session deleted successfully"
        });
        setSelectedSessions(prev => {
          const newSet = new Set(prev);
          newSet.delete(sessionId);
          return newSet;
        });
        loadSessions();
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: result.error || "Failed to delete session"
        });
      }
    } catch (error) {
      console.error('Error deleting session:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete session"
      });
    }
  };

  const handleBulkDelete = async () => {
    const selectedIds = Array.from(selectedSessions);
    if (selectedIds.length === 0) return;

    const confirmMessage = `Are you sure you want to delete ${selectedIds.length} selected session${selectedIds.length > 1 ? 's' : ''}?`;
    if (!confirm(confirmMessage)) return;

    setBulkDeleting(true);
    try {
      const result = await ReAnalysisHistoryService.deleteBulkSessions(selectedIds);
      if (result.success) {
        toast({
          title: "Success",
          description: `Successfully deleted ${result.deletedCount} session${(result.deletedCount || 0) > 1 ? 's' : ''}`
        });
        setSelectedSessions(new Set());
        loadSessions();
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: result.error || "Failed to delete sessions"
        });
      }
    } catch (error) {
      console.error('Error bulk deleting sessions:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete sessions"
      });
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedSessions.size === sessions.length) {
      setSelectedSessions(new Set());
    } else {
      setSelectedSessions(new Set(sessions.map(s => s.id)));
    }
  };

  const handleSelectSession = (sessionId: string, checked: boolean) => {
    setSelectedSessions(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(sessionId);
      } else {
        newSet.delete(sessionId);
      }
      return newSet;
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'running':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'cancelled':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      completed: 'default',
      running: 'secondary',
      failed: 'destructive',
      cancelled: 'outline'
    } as const;
    
    return (
      <Badge variant={variants[status as keyof typeof variants] || 'outline'}>
        {status}
      </Badge>
    );
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return 'N/A';
    const seconds = Math.round(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${Math.round(seconds / 3600)}h`;
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Re-Analysis Sessions
          </CardTitle>
          <CardDescription>
            View and manage all re-analysis sessions with detailed tracking
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters & Bulk Actions */}
          <div className="flex gap-4 items-center justify-between">
            <div className="flex gap-4">
              <Select value={filters.status} onValueChange={(value) => setFilters(prev => ({ ...prev, status: value === 'all' ? undefined : value }))}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="running">Running</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={loadSessions}
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>

            {/* Bulk Actions */}
            {selectedSessions.size > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {selectedSessions.size} selected
                </span>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {bulkDeleting ? 'Deleting...' : `Delete (${selectedSessions.size})`}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedSessions(new Set())}
                >
                  Clear
                </Button>
              </div>
            )}
          </div>

          {/* Sessions Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={sessions.length > 0 && selectedSessions.size === sessions.length}
                      onCheckedChange={handleSelectAll}
                      disabled={sessions.length === 0}
                    />
                  </TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Patterns</TableHead>
                  <TableHead>Changes</TableHead>
                  <TableHead>Bot</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                      Loading sessions...
                    </TableCell>
                  </TableRow>
                ) : sessions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No re-analysis sessions found
                    </TableCell>
                  </TableRow>
                ) : (
                  sessions.map((session) => (
                    <TableRow key={session.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedSessions.has(session.id)}
                          onCheckedChange={(checked) => handleSelectSession(session.id, checked as boolean)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getStatusIcon(session.status)}
                          {getStatusBadge(session.status)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {formatDistanceToNow(new Date(session.started_at), { addSuffix: true })}
                        </div>
                      </TableCell>
                      <TableCell>
                        {formatDuration(session.processing_time_ms)}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div className="font-medium">{session.patterns_found || 0} processed</div>
                          <div className="text-muted-foreground text-xs">
                            in {session.processing_time_ms ? `${(session.processing_time_ms / 1000).toFixed(1)}s` : 'N/A'}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div className="text-green-600">+{session.patterns_created || 0}</div>
                          <div className="text-blue-600">~{session.patterns_updated || 0}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm font-medium">
                          {(session as any).bots?.name || 'Unknown Bot'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetails(session)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteSession(session.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Re-Analysis Session Details</DialogTitle>
            <DialogDescription>
              Detailed information about the re-analysis session
            </DialogDescription>
          </DialogHeader>
          
          {selectedSession && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Status</label>
                  <div className="mt-1">
                    {getStatusBadge(selectedSession.status)}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Processing Time</label>
                  <div className="mt-1 text-sm">
                    {formatDuration(selectedSession.processing_time_ms)}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Trades Analyzed</label>
                  <div className="mt-1 text-sm">
                    {selectedSession.total_trades_analyzed}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Patterns Found</label>
                  <div className="mt-1 text-sm">
                    {selectedSession.patterns_found}
                  </div>
                </div>
              </div>

              {/* Pattern Comparison Section - Disabled (backup removed for optimization) */}
              {false && (
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium">Pattern Changes</h4>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (selectedSession && onSessionSelect) {
                          onSessionSelect(selectedSession.id, selectedSession.trading_file_id);
                        }
                      }}
                      disabled={loadingComparison}
                    >
                      View Detailed Changes
                    </Button>
                  </div>
                  
                  {loadingComparison ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                      <span className="text-sm text-muted-foreground">Loading comparison data...</span>
                    </div>
                  ) : comparisonData ? (
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium">Total Backup Patterns:</span> {comparisonData.totalBackupPatterns}
                      </div>
                      <div>
                        <span className="font-medium">Total Current Patterns:</span> {comparisonData.totalCurrentPatterns}
                      </div>
                      <div className="text-green-600">
                        <span className="font-medium">Created:</span> {comparisonData.created}
                      </div>
                      <div className="text-blue-600">
                        <span className="font-medium">Updated:</span> {comparisonData.updated}
                      </div>
                      <div className="text-gray-600">
                        <span className="font-medium">Unchanged:</span> {comparisonData.unchanged}
                      </div>
                      <div className="text-red-600">
                        <span className="font-medium">Deleted:</span> {comparisonData.deleted}
                      </div>
                      
                      {(comparisonData.updated === 0 && comparisonData.unchanged === 0) && (
                        <div className="col-span-2 text-sm text-amber-600 bg-amber-50 p-2 rounded mt-2">
                          <AlertTriangle className="h-4 w-4 inline mr-1" />
                          Large dataset detected - summary shows estimates. Use "View Detailed Changes" for complete analysis.
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      Failed to load comparison data
                    </div>
                  )}
                </div>
              )}

              {selectedSession.error_message && (
                <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                  <h4 className="font-medium text-red-800 mb-2">Error Message</h4>
                  <p className="text-sm text-red-700">{selectedSession.error_message}</p>
                </div>
              )}

              {selectedSession.success_message && (
                <div className="border border-green-200 rounded-lg p-4 bg-green-50">
                  <h4 className="font-medium text-green-800 mb-2">Success Message</h4>
                  <p className="text-sm text-green-700">{selectedSession.success_message}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};